// Pure client-side logic for the today's-schedule header widget
// (ScheduleWidget.jsx) -- turning Schedule Hub's raw "HH:MM" shift/break/
// lunch data into a live, human-readable status. No `Date` object is used
// anywhere in this file: these are wall-clock times with no date of their
// own, so string/integer math on "HH:MM" is exact where running them
// through `Date` (and its timezone/DST baggage) would just be a source of
// bugs for no benefit.

// "HH:MM" -> minutes since midnight, or null for a null/undefined input
// (Schedule Hub returns null shift_start/shift_end for a real employee
// with no schedule that day).
export function toMinutes(hhmm) {
  if (hhmm == null) return null
  const [h, m] = hhmm.split(':').map(Number)
  return h * 60 + m
}

// "HH:MM" (24h) -> "11:30 AM" (12h). Returns null for a null input so
// callers can decide their own fallback text rather than this function
// guessing one.
export function formatTime12h(hhmm) {
  if (hhmm == null) return null
  const [h, m] = hhmm.split(':').map(Number)
  const period = h < 12 ? 'AM' : 'PM'
  const h12 = h % 12 === 0 ? 12 : h % 12
  return `${h12}:${String(m).padStart(2, '0')} ${period}`
}

// Display label for every activity `type` Schedule Hub's admin schedule
// editor supports (see the color legend in WFM's own Schedule view) --
// keyed by the lowercase activity code the `activities` field below uses.
const ACTIVITY_LABELS = {
  absent: 'Absent',
  bot_qa: 'Bot QA',
  break: 'Break',
  coaching: 'Coaching',
  end_of_shift: 'End of Shift',
  fmla: 'FMLA',
  holiday: 'Holiday',
  in_queue_working: 'In Queue Working',
  late_arrival: 'Late Arrival',
  leave_early: 'Leave Early',
  lunch: 'Lunch',
  meeting: 'Meeting',
  overtime: 'OT',
  project_loan: 'Project/Loan',
  pto: 'PTO',
  spanish_callback: 'Unavailable Spanish Callback',
  split: 'Split Shift',
  teaching_mentor: 'Teaching/Mentor',
  technical_issues: 'Technical Issues',
  training: 'Training',
  vto: 'VTO',
}

// Every scheduled activity for the day, in chronological order, each with a
// `label` for display and a `note` (null if none). Schedule Hub's `activities`
// field (when present) already covers break/lunch too, so it's the single
// source of truth once it exists; `breaks`/`lunch` are merged as a fallback
// for a response that predates that field (or hasn't backfilled it yet) --
// same reasoning ui/src/handleTimeAggregate.js gives for defensive fallbacks
// on an external contract this file doesn't control the rollout of.
export function scheduleEvents(schedule) {
  if (Array.isArray(schedule.activities)) {
    return [...schedule.activities]
      .map((a) => ({ ...a, label: ACTIVITY_LABELS[a.type] ?? a.type, note: a.note ?? null }))
      .sort((a, b) => toMinutes(a.start_time) - toMinutes(b.start_time))
  }
  const breaks = (schedule.breaks ?? []).map((b) => ({ ...b, type: 'break', label: 'Break', note: null }))
  const lunch = (schedule.lunch ?? []).map((l) => ({ ...l, type: 'lunch', label: 'Lunch', note: null }))
  return [...breaks, ...lunch].sort((a, b) => toMinutes(a.start_time) - toMinutes(b.start_time))
}

// 'past' (already ended) | 'current' (happening now) | 'upcoming', for
// styling a single {start_time,end_time} event in the popover list --
// exclusive end, same boundary convention computeScheduleStatus uses below.
export function eventTiming(event, nowHHMM) {
  const now = toMinutes(nowHHMM)
  if (now >= toMinutes(event.end_time)) return 'past'
  if (now >= toMinutes(event.start_time)) return 'current'
  return 'upcoming'
}

// Current wall-clock "HH:MM" in the given IANA timezone -- the same
// Intl.DateTimeFormat technique server/src/repositories/reporting.js uses
// for its own day/time math, duplicated here for the same "ui has no
// server import" reason ui/src/handleTimeAggregate.js documents.
// now: optional, for tests (the reminder's timezone handling); defaults to now.
export function nowHHMMInZone(timezone, now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone, hour12: false, hour: '2-digit', minute: '2-digit',
  }).formatToParts(now)
  const get = (type) => parts.find((p) => p.type === type)?.value
  // hour12:false renders midnight as '24' in some ICU versions -- % 24 it,
  // same defensive fix reporting.js's tzOffsetMs applies for the same reason.
  const hour = String(Number(get('hour')) % 24).padStart(2, '0')
  return `${hour}:${get('minute')}`
}

// schedule: Schedule Hub's raw response shape ({ day_off, shift_start,
// shift_end, breaks: [{start_time,end_time}], lunch: [...],
// activities: [{type,start_time,end_time,note}], notes }).
// nowHHMM: "HH:MM" in the SAME timezone schedule.timezone describes
// (callers should pass nowHHMMInZone(schedule.timezone)).
//
// Returns { label, kind }, kind one of:
//   'off' | 'no-data' | 'before' | 'lunch' | 'break' | 'activity' | 'in-shift' | 'after'
//
// Known limitation, deliberately not handled: an overnight shift (shift_end
// earlier than shift_start, crossing midnight) would be read as already-
// ended rather than in-progress -- Schedule Hub's contract doesn't
// currently describe overnight shifts, and this codebase avoids building
// for a case that isn't real yet. Revisit if that ever changes.
export function computeScheduleStatus(schedule, nowHHMM) {
  if (schedule.day_off) return { label: 'Off today', kind: 'off' }

  const shiftStart = toMinutes(schedule.shift_start)
  const shiftEnd = toMinutes(schedule.shift_end)
  if (shiftStart == null || shiftEnd == null) {
    return { label: 'No schedule today', kind: 'no-data' }
  }

  const now = toMinutes(nowHHMM)

  if (now < shiftStart) {
    return { label: `Starts at ${formatTime12h(schedule.shift_start)}`, kind: 'before' }
  }
  if (now >= shiftEnd) {
    return { label: 'Shift ended', kind: 'after' }
  }

  // "On break"/"On lunch" read naturally as an ongoing status; every other
  // activity type (Meeting, Coaching, Training, ...) instead gets its plain
  // label ("Meeting until 2:30 PM") -- both share one lookup over the same
  // chronological event list rather than break/lunch keeping separate,
  // duplicate loops now that other activity types exist too.
  const CURRENT_LABEL_PREFIX = { break: 'On break', lunch: 'On lunch' }
  const events = scheduleEvents(schedule)
  const current = events.find((e) => now >= toMinutes(e.start_time) && now < toMinutes(e.end_time))
  if (current) {
    const prefix = CURRENT_LABEL_PREFIX[current.type] ?? current.label
    const kind = current.type === 'break' || current.type === 'lunch' ? current.type : 'activity'
    return { label: `${prefix} until ${formatTime12h(current.end_time)}`, kind }
  }

  // Not currently on a break/lunch/other activity -- report the NEXT one
  // coming up rather than the shift end, so the chip answers "what should I
  // watch for" (the thing an agent actually needs a heads-up on) instead of
  // "when do I leave" (which is comparatively far off and already visible in
  // the popover). Only falls back to the shift-end label once every
  // scheduled activity for the day is already behind `now`.
  const next = events.find((e) => toMinutes(e.start_time) > now)
  if (next) {
    return { label: `${next.label} at ${formatTime12h(next.start_time)}`, kind: 'in-shift' }
  }
  return { label: `In shift until ${formatTime12h(schedule.shift_end)}`, kind: 'in-shift' }
}
